import { nextFrame } from 'helpers/timing';
import useResetForm from 'stimulus-hooks/use-reset-form';
import ApplicationController from './application_controller';

export default class extends ApplicationController {
  static targets = [
    'subscriberRelationSelect',
    'subscriberFirstName',
    'subscriberFirstNameInput',
    'subscriberMiddleName',
    'subscriberLastName',
    'subscriberLastNameInput',
    'subscriberSuffix',
    'subscriberDateOfBirth',
    'subscriberDateOfBirthInput',
    'subscriberSex',
    'subscriberSexInput',
    'active',
    'startDate',
    'endDate',
  ];

  static values = { today: String };

  connect() {
    useResetForm(this);
    this.startDateChanged();
    this.requireEndDateOfInactiveCoverage();
  }

  /**
   * A coverage already inactive with no end date predates the rule, and the server lets it be
   * edited as it is. The form asks for the date on its next edit instead, with nothing pre-filled:
   * a date guessed for an old plan would read as a coverage window to the readers that trust a
   * recorded end date, so the user chooses one, or deletes the coverage.
   */
  requireEndDateOfInactiveCoverage() {
    if (!this.hasActiveTarget || !this.hasEndDateTarget) return;
    if (this.activeTarget.checked || this.endDateTarget.value) return;

    this.endDateTarget.required = true;
  }

  // The server refuses an end date before the start date, so the browser refuses it first.
  startDateChanged() {
    if (!this.hasStartDateTarget || !this.hasEndDateTarget) return;

    this.endDateTarget.min = this.startDateTarget.value;
  }

  relationToSubscriberChanged(event) {
    const { target } = event.detail;
    const value = target.getAttribute('value');
    this.toggleSubscriberFieldVisibility(value);
  }

  /**
   * Unticking Active asks for the end date in the same breath. A coverage is placed in time by
   * its dates, so one left inactive and undated still reads as covering every earlier date of
   * service, and the server refuses the save. Suggests today in the viewer's timezone, which the
   * form supplies. The field stays editable: a coverage that ended earlier takes its real date.
   *
   * A coverage whose start date is still ahead gets no suggestion. It never applied, and a date
   * written for it would read as a coverage window once that day has passed, in the readers that
   * trust a recorded end date over the checkbox (PatientPaymentMethod.active_or_historically_ended).
   * The field is required and empty, so the user enters a date on purpose or deletes the coverage,
   * which the help text points to.
   *
   * Re-ticking undoes only what this controller did. The suggestion is removed while the field
   * still holds it, so a mistaken untick does not end an active plan today; a date the user
   * typed, or one already on file, is left alone.
   */
  activeChanged() {
    if (!this.hasActiveTarget || !this.hasEndDateTarget) return;

    if (this.activeTarget.checked) {
      this.endDateTarget.required = false;
      if (this.suggestedEndDate && this.endDateTarget.value === this.suggestedEndDate) {
        this.endDateTarget.value = '';
      }
      this.suggestedEndDate = null;
      return;
    }

    this.endDateTarget.required = true;
    if (!this.endDateTarget.value && !this.coverageNotStarted()) {
      this.suggestedEndDate = this.todayValue;
      this.endDateTarget.value = this.suggestedEndDate;
    }
  }

  /**
   * The end date is the one required control inside the collapsed Advanced options panel. A
   * required control inside a hidden panel cannot be focused for native validation, so the
   * browser blocks the submit and shows nothing. Opening the panel on `invalid` lets the browser
   * focus the field and show its message; the accordion unhides the panel before its transition.
   */
  revealEndDate() {
    const accordion = this.endDateTarget.closest('accordion-item');
    if (accordion && !accordion.hasAttribute('open')) accordion.setAttribute('open', '');
  }

  async resetForm() {
    // Form reset is processed in the next animation frame.
    await nextFrame();
    if (this.relationIsSelf(this.subscriberRelationSelectTarget.value)) {
      this.hideSubscriberNameFields();
    }
  }

  /**
   * @private
   */
  coverageNotStarted() {
    const startDate = this.hasStartDateTarget ? this.startDateTarget.value : '';
    return startDate > this.todayValue;
  }

  /**
   * @private
   */
  toggleSubscriberFieldVisibility(value) {
    if (this.relationIsSelf(value)) {
      this.hideSubscriberNameFields();
    } else {
      this.showSubscriberNameFields();
    }
  }

  /**
   * @private
   */
  showSubscriberNameFields() {
    this.subscriberFirstNameTarget.hidden = false;
    this.subscriberFirstNameInputTarget.required = true;
    this.subscriberMiddleNameTarget.hidden = false;
    this.subscriberLastNameTarget.hidden = false;
    this.subscriberLastNameInputTarget.required = true;
    this.subscriberSuffixTarget.hidden = false;
    this.subscriberDateOfBirthTarget.hidden = false;
    this.subscriberSexTarget.hidden = false;
  }

  /**
   * @private
   */
  hideSubscriberNameFields() {
    this.subscriberFirstNameTarget.hidden = true;
    this.subscriberFirstNameInputTarget.required = false;
    this.subscriberMiddleNameTarget.hidden = true;
    this.subscriberLastNameTarget.hidden = true;
    this.subscriberLastNameInputTarget.required = false;
    this.subscriberSuffixTarget.hidden = true;
    this.subscriberDateOfBirthTarget.hidden = true;
    this.subscriberSexTarget.hidden = true;
  }

  /**
   * @private
   */
  relationIsSelf(value) {
    return value === 'self';
  }
}
